import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { CrackAnalysis, Severity, InspectionMode, Finding, TelemetryPoint, ImageResolutionMode, IMAGE_RESOLUTION_OPTIONS } from '../types';
import { analyzeSteelSequence, analyzeStaticSurface, analyzeTwoPass } from '../services/claudeService';
import { parseTrajectoryCsv, detectMissionStart, calculateAssetGeometry, parseLasFile, parseTextPointCloud, filterPointsToAssetBoundary } from '../services/telemetryService';
import { TrajectoryMapM } from './TrajectoryMapM';
import { exportFindingsToZip } from '../services/zipService';
import { saveFileToProject, saveSessionCheckpoint } from '../services/fileSystemService';
import { saveEntryToVault } from '../services/vaultService';
import { LoadingOverlay } from './LoadingOverlay';

interface VideoInspectorProps {
  onComplete: (videoUrl: string, timeline: CrackAnalysis[], videoName: string, evaluationDuration: number, fps: number, range: [number, number], metadata: any) => void;
  onCancel: () => void;
  toolSpecs?: string;
  toolFps?: number;
  toolId?: string;
  initialModes?: InspectionMode[];
  initialFile?: File;
  initialRange?: [number, number];
  initialTrajectoryPoints?: TelemetryPoint[];
  initialLasModelName?: string;
  initialModelPoints?: TelemetryPoint[];
  initialOverlap?: number;
  initialGeometry?: any;
  initialTimeline?: CrackAnalysis[];
  initialCloudOffset?: { x: number, y: number };
}

interface FlightSegment {
  id: string;
  video: File;
  videoUrl: string;
  trajectory: TelemetryPoint[];
  duration: number;
  pointCount: number;
}

const severityColors: Record<Severity, string> = {
  [Severity.NONE]: '#94a3b8',
  [Severity.LOW]: '#0000a9',
  [Severity.MEDIUM]: '#d97706',
  [Severity.HIGH]: '#ea580c',
  [Severity.CRITICAL]: '#dc2626',
};

const PROTOCOL_OPTIONS = [
  { id: InspectionMode.CRACK_ONLY, label: 'Crack Detection', icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>, desc: 'Cracks & deep scratches.' },
  { id: InspectionMode.WELD_INTEGRITY, label: 'Weld Integrity', icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>, desc: 'Junctions & HAZ audit.' },
  { id: InspectionMode.GENERAL_NDT, label: 'Structural Audit', icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>, desc: 'Cracks, fissures, corrosion & dents.' },
  { id: InspectionMode.FOULING_INSPECTION, label: 'Cleanliness', icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.691.31a2 2 0 01-1.782 0l-.691-.31a6 6 0 00-3.86-.517l-2.388.477a2 2 0 00-1.022.547l-.34.34a2 2 0 000 2.829l1.245 1.244a2 2 0 002.829 0l.144-.144a2 2 0 000-2.829l-1.245-1.244" /></svg>, desc: 'Residue & Scaling.' }
];

export const VideoInspectorM: React.FC<VideoInspectorProps> = ({
  onComplete, onCancel, toolSpecs, toolFps = 30, toolId, initialModes = [InspectionMode.GENERAL_NDT], initialFile, initialRange, initialTrajectoryPoints, initialLasModelName, initialModelPoints, initialOverlap, initialGeometry, initialTimeline, initialCloudOffset
}) => {
  const [segments, setSegments] = useState<FlightSegment[]>([]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(0);
  const [pendingVideo, setPendingVideo] = useState<File | null>(initialFile || null);
  const [pendingTrajectory, setPendingTrajectory] = useState<TelemetryPoint[] | null>(initialTrajectoryPoints && initialTrajectoryPoints.length > 0 ? initialTrajectoryPoints : null);
  const [pendingTrajectoryFile, setPendingTrajectoryFile] = useState<File | null>(null);
  const currentSegment = segments[activeSegmentIndex];

  const isSimulation = useMemo(() => !!initialTrajectoryPoints && initialTrajectoryPoints.length > 0, [initialTrajectoryPoints]);

  const videoUrl = currentSegment?.videoUrl || (initialFile ? URL.createObjectURL(initialFile) : null);
  const videoFile = currentSegment?.video || initialFile;
  const trajectoryPoints = currentSegment?.trajectory || initialTrajectoryPoints || null;
  const isUAV = toolId === 'Elios3 UAV Chambers';

  const [isProcessing, setIsProcessing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [inspectionModes, setInspectionModes] = useState<InspectionMode[]>(initialModes);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(false);
  const [isAborting, setIsAborting] = useState(false);
  const stopNeuralEngine = () => { abortRef.current = true; setIsAborting(true); };
  const [detectedFps, setDetectedFps] = useState<number>(30);
  const [elapsedClock, setElapsedClock] = useState<number>(0);
  const [finalTimeline, setFinalTimeline] = useState<CrackAnalysis[]>([]);
  const [sensitivity, setSensitivity] = useState(5);
  const [resolutionMode, setResolutionMode] = useState<ImageResolutionMode>(ImageResolutionMode.STANDARD);
  const [forwardOverlap, setForwardOverlap] = useState(initialOverlap ?? 25);
  const [syncOffset, setSyncOffset] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isArchiving, setIsArchiving] = useState(false);

  // Bypasses initiation screen if simulation data is provided from Mission Planner
  const [complianceAccepted, setComplianceAccepted] = useState(!!initialFile || isSimulation);

  const [rangeStart, setRangeStart] = useState(initialRange ? initialRange[0] : 0);
  const [rangeEnd, setRangeEnd] = useState(initialRange ? initialRange[1] : 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1.0);
  const [segmentRanges, setSegmentRanges] = useState<Record<string, [number, number]>>({});
  const [debouncedTime, setDebouncedTime] = useState(currentTime);
  const [missionStartTime, setMissionStartTime] = useState<number | undefined>(initialTrajectoryPoints && initialTrajectoryPoints.length > 0 ? initialTrajectoryPoints[0].timestamp : undefined);
  const [showCapOnly, setShowCapOnly] = useState(false);

  const trajectoryDuration = useMemo(() => {
    if (!trajectoryPoints || trajectoryPoints.length < 2) return 0;
    return trajectoryPoints[trajectoryPoints.length - 1].timestamp - trajectoryPoints[0].timestamp;
  }, [trajectoryPoints]);

  const [lasModelName, setLasModelName] = useState<string | null>(initialLasModelName || null);
  const [modelPoints, setModelPoints] = useState<TelemetryPoint[]>(initialModelPoints || []);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [sessionFindings, setSessionFindings] = useState<CrackAnalysis[]>(initialTimeline || []);
  const [verifiedFindingsIdx, setVerifiedFindingsIdx] = useState<Set<number>>(new Set((initialTimeline || []).map((_, i) => i)));
  const [lastAuditStatus, setLastAuditStatus] = useState<"IDLE" | "SAMPLING" | "THINKING" | "RESOLVED" | "WAITING">("IDLE");
  const [activeFindings, setActiveFindings] = useState<Finding[]>([]);

  useEffect(() => {
    if (initialTimeline && initialTimeline.length > 0) {
      setSessionFindings(initialTimeline);
      setFinalTimeline(initialTimeline);
      setVerifiedFindingsIdx(new Set(initialTimeline.map((_, i) => i)));
    }
  }, [initialTimeline]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const playbackIntervalRef = useRef<any>(null);
  const stageVideoRef = useRef<HTMLInputElement>(null);
  const stageCsvRef = useRef<HTMLInputElement>(null);
  const lasInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = simulationSpeed; }, [simulationSpeed]);
  useEffect(() => { if (trajectoryPoints && trajectoryPoints.length > 0) { const logStart = trajectoryPoints[0].timestamp; const missionStartVal = detectMissionStart(trajectoryPoints); const calculatedOffset = (missionStartVal - logStart) - 2.5; setSyncOffset(Number(calculatedOffset.toFixed(1))); setMissionStartTime(logStart); } }, [trajectoryPoints]);
  useEffect(() => { setSegmentRanges(prev => { const next = { ...prev }; let changed = false; segments.forEach(s => { if (!next[s.id]) { next[s.id] = [0, s.duration]; changed = true; } }); return changed ? next : prev; }); }, [segments]);

  const combinedTrajectoryData = useMemo(() => {
    if (segments.length > 0) {
      let timeOffset = 0; const allPoints: TelemetryPoint[] = [], offsets: number[] = [];
      segments.forEach((seg) => {
        offsets.push(timeOffset); const sortedSegPoints = [...seg.trajectory].sort((a, b) => a.timestamp - b.timestamp); if (sortedSegPoints.length === 0) return;
        const firstT = sortedSegPoints[0].timestamp;
        const shifted = sortedSegPoints.map(p => ({ ...p, timestamp: (p.timestamp - firstT) + timeOffset })); allPoints.push(...shifted);
        const r = segmentRanges[seg.id] || [0, seg.duration]; timeOffset += Math.max(0, r[1] - r[0]) + 5.0;
      });
      return { points: allPoints, offsets };
    }
    return { points: trajectoryPoints || [], offsets: [0] };
  }, [segments, trajectoryPoints, segmentRanges]);

  const combinedTrajectory = combinedTrajectoryData.points;
  const segmentOffsets = combinedTrajectoryData.offsets;

  const currentGlobalMapTime = useMemo(() => {
    if (segments.length <= 1) return debouncedTime + syncOffset;
    const segmentOffset = combinedTrajectoryData.offsets[activeSegmentIndex] || 0;
    const r = segmentRanges[currentSegment?.id || ''] || [0, 0];
    return Math.max(0, debouncedTime - r[0]) + segmentOffset + syncOffset;
  }, [debouncedTime, syncOffset, segments.length, activeSegmentIndex, combinedTrajectoryData, segmentRanges, currentSegment]);

  const activeGlobalRange = useMemo(() => {
    if (segments.length <= 1) return [rangeStart + syncOffset, rangeEnd + syncOffset] as [number, number];
    const r = (currentSegment?.id ? segmentRanges[currentSegment.id] : null) || [0, currentSegment?.duration || 0];
    const o = segmentOffsets[activeSegmentIndex] || 0;
    return [o + r[0] + syncOffset, o + r[1] + syncOffset] as [number, number];
  }, [rangeStart, rangeEnd, syncOffset, activeSegmentIndex, segmentOffsets, segments.length, segmentRanges, currentSegment]);

  useEffect(() => { const h = setTimeout(() => { setDebouncedTime(currentTime); }, 50); return () => clearTimeout(h); }, [currentTime]);

  const canProceed = useMemo(() => segments.length > 0 || (isUAV ? (!!videoFile && !!trajectoryPoints) : !!videoFile), [videoFile, trajectoryPoints, isUAV, segments]);

  const toggleMode = (mode: InspectionMode) => setInspectionModes(prev => prev.includes(mode) ? (prev.length === 1 ? prev : prev.filter(m => m !== mode)) : [...prev, mode]);
  const formatTime = (seconds: number) => { const s = Math.max(0, Math.round(seconds)), m = Math.floor(s / 60); return `${m.toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`; };

  useEffect(() => {
    if (initialFile) {
      const url = URL.createObjectURL(initialFile);
      setSegments([{
        id: 'initial-transfer',
        video: initialFile,
        videoUrl: url,
        trajectory: initialTrajectoryPoints || [],
        duration: 0,
        pointCount: initialTrajectoryPoints?.length || 0
      }]);
      setComplianceAccepted(true);
    }
  }, [initialFile, initialTrajectoryPoints]);

  const handleMetadataLoaded = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
      if (isSimulation) setRangeEnd(trajectoryDuration);
      else if (activeSegmentIndex === 0 && (!initialRange || initialRange[1] === 0)) setRangeEnd(videoRef.current.duration);
      videoRef.current.currentTime = rangeStart % (videoRef.current.duration || 1);
      videoRef.current.playbackRate = simulationSpeed; setCurrentTime(rangeStart); setTimeout(drawPreviewFrame, 200);
    }
  };

  const drawPreviewFrame = useCallback(() => {
    const video = videoRef.current, canvas = previewCanvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) { if (video.videoWidth > 0 && video.videoHeight > 0) { canvas.width = video.videoWidth; canvas.height = video.videoHeight; } }
    ctx.fillStyle = '#000000'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (video.readyState >= 2) ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const scale = Math.max(1, canvas.width / 1280);
    if (isProcessing) {
      const padding = 20 * scale, startX = 20 * scale, startY = 20 * scale, headerSize = Math.floor(16 * scale), bodySize = Math.floor(11 * scale), lineHeight = Math.floor(19 * scale);
      const boxWidth = Math.max(280, 420 * scale), boxHeight = (padding * 2) + headerSize + (5 * lineHeight);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'; ctx.fillRect(startX, startY, boxWidth, boxHeight);
      ctx.fillStyle = '#06b6d4'; ctx.font = `bold ${headerSize}px JetBrains Mono`; ctx.fillText(`V6.0 SIMULATION HUD`, startX + padding, startY + padding + headerSize);
      ctx.fillStyle = '#ffffff'; ctx.font = `bold ${bodySize}px JetBrains Mono`;
      let cursorY = startY + padding + headerSize + lineHeight;
      ctx.fillText(`SIM SPEED: ${simulationSpeed.toFixed(1)}X`, startX + padding, cursorY); cursorY += lineHeight;
      ctx.fillText(`SENSITIVITY: ${sensitivity}/10 | OVERLAP: ${forwardOverlap}%`, startX + padding, cursorY); cursorY += lineHeight;
      ctx.fillText(`AUDIT STATUS: ${lastAuditStatus}`, startX + padding, cursorY);
    }

    if (activeFindings.length > 0) {
      activeFindings.forEach((finding, idx) => {
        const [ymin, xmin, ymax, xmax] = finding.box, color = severityColors[finding.severity] || '#fff';
        const x = (xmin / 1000) * canvas.width, y = (ymin / 1000) * canvas.height, w = ((xmax - xmin) / 1000) * canvas.width, h = ((ymax - ymin) / 1000) * canvas.height;
        ctx.strokeStyle = color; ctx.lineWidth = 4 * scale; ctx.strokeRect(x, y, w, h);
        ctx.fillStyle = color; ctx.font = `bold ${Math.max(12, 16 * scale)}px JetBrains Mono`;
        ctx.fillText(`ANOMALY: ${finding.severity.toUpperCase()}`, x, y - (10 * scale));

        // Corners
        const cornerSize = 20 * scale;
        ctx.beginPath();
        ctx.moveTo(x, y + cornerSize); ctx.lineTo(x, y); ctx.lineTo(x + cornerSize, y);
        ctx.moveTo(x + w - cornerSize, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w, y + cornerSize);
        ctx.moveTo(x + w, y + h - cornerSize); ctx.lineTo(x + w, y + h); ctx.lineTo(x + w - cornerSize, y + h);
        ctx.moveTo(x + cornerSize, y + h); ctx.lineTo(x, y + h); ctx.lineTo(x, y + h - cornerSize);
        ctx.stroke();
      });
    }
  }, [isProcessing, sensitivity, forwardOverlap, lastAuditStatus, simulationSpeed, activeFindings]);

  useEffect(() => { const updateLoop = () => { drawPreviewFrame(); rafRef.current = requestAnimationFrame(updateLoop); }; rafRef.current = requestAnimationFrame(updateLoop); return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }; }, [drawPreviewFrame]);

  useEffect(() => {
    let intervalId: any = null;
    if (isPlaying) { intervalId = window.setInterval(() => { if (videoRef.current) { const t = currentTime + (0.05 * simulationSpeed); if (t >= rangeEnd) { togglePlayback(false); setCurrentTime(rangeEnd); } else { setCurrentTime(t); setDebouncedTime(t); } } }, 50); playbackIntervalRef.current = intervalId; }
    else if (playbackIntervalRef.current) { window.clearInterval(playbackIntervalRef.current); playbackIntervalRef.current = null; }
    return () => { if (playbackIntervalRef.current) { window.clearInterval(playbackIntervalRef.current); playbackIntervalRef.current = null; } };
  }, [isPlaying, rangeEnd, currentTime, simulationSpeed]);

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>, isStart: boolean) => {
    const val = parseFloat(e.target.value), v = videoRef.current; if (!v) return;
    if (!v.paused) { v.pause(); setIsPlaying(false); }
    if (isStart) { const f = Math.min(val, rangeEnd - 0.1); setRangeStart(f); if (v.duration) v.currentTime = f % v.duration; setCurrentTime(f); }
    else { const f = Math.max(val, rangeStart + 0.1); setRangeEnd(f); if (v.duration) v.currentTime = f % v.duration; setCurrentTime(f); }
  };
  const togglePlayback = (force?: boolean) => { const v = videoRef.current; if (!v) return; const p = force !== undefined ? force : !isPlaying; if (p) { if (currentTime >= rangeEnd) setCurrentTime(rangeStart); v.play().catch(e => console.error(e)); setIsPlaying(true); } else { v.pause(); setIsPlaying(false); } };
  const stopPlayback = () => { const v = videoRef.current; if (!v) return; v.pause(); setCurrentTime(rangeStart); if (v.duration) v.currentTime = rangeStart % v.duration; setIsPlaying(false); };
  const handleAbort = () => { stopPlayback(); onCancel(); };

  const handleLasUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLasModelName(file.name);
      setIsArchiving(true);
      let pts: TelemetryPoint[] = [];
      try {
        // Auto-save PointCloud to Local Project
        await saveFileToProject('04_Telemetry_Data', file.name, file);

        const ext = file.name.toLowerCase().split('.').pop();
        if (ext === 'las' || ext === 'laz') pts = await parseLasFile(file); else pts = await parseTextPointCloud(file);

        if (pts.length > 0) {
          const geom = calculateAssetGeometry(pts);
          const filtered = filterPointsToAssetBoundary(pts, geom, 0.5);
          setModelPoints(filtered);
        }
      } catch (e) { alert("Import failed."); }
      finally {
        setIsArchiving(false);
      }
      // Session Checkpoint
      saveSessionCheckpoint({
        lasModelName: file.name,
        modelPoints: pts,
        view: 'VIDEO_INSPECTION_M',
        modes: inspectionModes
      });
    }
  };

  const handleStageVideo = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) setPendingVideo(file); };
  const handleStageCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingTrajectoryFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const pts = parseTrajectoryCsv(event.target?.result as string);
      if (pts.length > 0) setPendingTrajectory(pts);
      else alert("Invalid Telemetry.");
    };
    reader.readAsText(file);
  };

  const addFlightSegment = async () => {
    if (pendingVideo && (pendingTrajectory || !isUAV)) {
      const videoFile = pendingVideo;
      const csvFile = pendingTrajectoryFile;
      const url = URL.createObjectURL(videoFile);

      setIsArchiving(true);
      try {
        // Auto-save assets to Project Folder (V6.1)
        await saveFileToProject('03_Video_Logs', videoFile.name, videoFile);
        if (csvFile) {
          await saveFileToProject('04_Telemetry_Data', csvFile.name, csvFile);
        }

        // Save session checkpoint
        saveSessionCheckpoint({
          videoFile,
          trajectory: pendingTrajectory,
          range: [rangeStart, rangeEnd],
          view: 'VIDEO_INSPECTION_M',
          modes: inspectionModes
        });
      } finally {
        setIsArchiving(false);
      }

      const temp = document.createElement('video');
      temp.src = url;
      temp.onloadedmetadata = () => {
        setSegments(prev => [...prev, {
          id: crypto.randomUUID(),
          video: videoFile,
          videoUrl: url,
          trajectory: pendingTrajectory || [],
          duration: temp.duration,
          pointCount: pendingTrajectory?.length || 0
        }]);
        setPendingVideo(null);
        setPendingTrajectory(null);
        setPendingTrajectoryFile(null);
        if (stageVideoRef.current) stageVideoRef.current.value = "";
        if (stageCsvRef.current) stageCsvRef.current.value = "";
      };
    }
  };
  const removeSegment = (id: string) => setSegments(prev => prev.filter(s => s.id !== id));

  const seekToTime = (v: HTMLVideoElement, t: number): Promise<void> => new Promise(res => { const target = Math.max(0, Math.min(t, v.duration)), onS = () => { v.removeEventListener('seeked', onS); res(); }; v.addEventListener('seeked', onS); v.currentTime = target; });
  const captureFrameForAnalysis = (v: HTMLVideoElement, c: HTMLCanvasElement): string | null => { const ctx = c.getContext('2d'); if (!ctx || v.videoWidth === 0) return null; c.width = v.videoWidth; c.height = v.videoHeight; ctx.drawImage(v, 0, 0, c.width, c.height); return c.toDataURL('image/jpeg', 0.92); };

  const removeFinding = (idx: number) => {
    setSessionFindings(prev => prev.filter((_, i) => i !== idx));
    setVerifiedFindingsIdx(prev => {
      const next = new Set<number>();
      prev.forEach(v => { if (v > idx) next.add(v - 1); else if (v < idx) next.add(v); });
      return next;
    });
  };

  const verifyFinding = async (idx: number) => {
    const analysis = sessionFindings[idx];
    const isAdding = !verifiedFindingsIdx.has(idx);

    setVerifiedFindingsIdx(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });

    if (isAdding && analysis.frameImage) {
      // INSTANT PERSISTENCE: Save to Verified (02_) and Ground Truth (06_)
      await saveEntryToVault({
        image: analysis.frameImage,
        timestamp: analysis.timestamp || Date.now(),
        analysis: analysis,
        isVerified: true
      });
    }
  };

  const runDeepAnalysis = async (append: boolean = false) => {
    if (!videoRef.current || !canvasRef.current || segments.length === 0) return;
    abortRef.current = false;
    setIsAborting(false);
    stopPlayback(); setIsProcessing(true); setIsFinished(false); setProgress(0);
    if (!append) {
      setSessionFindings([]); setFinalTimeline([]);
    }
    const video = videoRef.current, canvas = canvasRef.current;
    const timeline: CrackAnalysis[] = [], temporalOffset = 2.0 / detectedFps;
    for (let segIdx = 0; segIdx < segments.length; segIdx++) {
      if (abortRef.current) break;
      const seg = segments[segIdx]; setActiveSegmentIndex(segIdx);
      if (videoRef.current.src !== seg.videoUrl) { videoRef.current.src = seg.videoUrl; await new Promise(res => { videoRef.current!.onloadedmetadata = res; }); }
      let scanTimestamps: number[] = [], tStart = (segments.length === 1) ? rangeStart : (segmentRanges[seg.id]?.[0] || 0), tEnd = (segments.length === 1) ? rangeEnd : (segmentRanges[seg.id]?.[1] || seg.duration);
      const step = Math.max(0.1, 4.0 * (1 - forwardOverlap / 100));
      for (let t = tStart; t < tEnd; t += step) scanTimestamps.push(t);
      for (let i = 0; i < scanTimestamps.length; i++) {
        if (abortRef.current) break;
        const targetTime = scanTimestamps[i]; setCurrentTime(targetTime); setLastAuditStatus("SAMPLING"); setProgress(((segIdx / segments.length) * 100) + ((i / scanTimestamps.length) * (100 / segments.length)));
        await seekToTime(video, targetTime); const fCurr = captureFrameForAnalysis(video, canvas);
        if (fCurr) {
          setLastAuditStatus("THINKING");
          try {
            const ctxGap = Math.min(1.5, (tEnd - tStart) * 0.10);
            await seekToTime(video, Math.max(tStart, targetTime - ctxGap)); const fPrev = captureFrameForAnalysis(video, canvas);
            await seekToTime(video, Math.min(tEnd, targetTime + ctxGap)); const fNext = captureFrameForAnalysis(video, canvas);
            await seekToTime(video, targetTime); // restore HUD to scan position
            const analysis = await analyzeTwoPass(fPrev || fCurr, fCurr, fNext || fCurr, inspectionModes, toolSpecs, sensitivity, 6000, 'gemini-2.5-pro', undefined, resolutionMode);
            const enrichedAnalysis = { ...analysis, timestamp: targetTime, frameImage: fCurr, videoName: seg.video.name, mode: analysis.mode || inspectionModes[0] };
            if (analysis.detected) {
              setSessionFindings(prev => [enrichedAnalysis, ...prev]);

              // Auto-save finding evidence to Local Project
              if (fCurr) {
                const base64Data = fCurr.split(',')[1];
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'image/jpeg' });
                const baseName = `finding_${Math.floor(targetTime)}s_${seg.video.name}`;
                await saveFileToProject('01_Evidence_Images', `${baseName}.jpg`, blob);

                const metaBlob = new Blob([JSON.stringify({
                  ...enrichedAnalysis,
                  id: `ev-${Date.now()}-${Math.floor(targetTime)}`,
                  projectId: 'local'
                }, null, 2)], { type: 'application/json' });
                await saveFileToProject('01_Evidence_Images', `${baseName}.json`, metaBlob);
              }

              // PERSISTENCE POINT: Save intermediate session progress
              saveSessionCheckpoint({
                videoFile: segments.length > 0 ? segments[0].video : videoFile,
                trajectory: combinedTrajectory,
                range: [rangeStart, rangeEnd],
                view: 'VIDEO_INSPECTION_M',
                modes: inspectionModes,
                timeline: [...finalTimeline, ...timeline, enrichedAnalysis],
                lasModelName,
                modelPoints,
                geometry: initialGeometry
              });
            }
            timeline.push(enrichedAnalysis); setLastAuditStatus("RESOLVED");
          } catch (err) { setLastAuditStatus("WAITING"); await new Promise(r => setTimeout(r, 1000)); }
        }
      }
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsProcessing(false);
    setIsAborting(false);
    setIsFinished(true);
    const updatedTimeline = append ? [...finalTimeline, ...timeline] : timeline;
    setFinalTimeline(updatedTimeline);

    // PERSISTENCE POINT: Save session checkpoint when analysis completes
    saveSessionCheckpoint({
      videoFile: segments.length > 0 ? segments[0].video : videoFile,
      trajectory: combinedTrajectory,
      range: [rangeStart, rangeEnd],
      view: 'VIDEO_INSPECTION_M',
      modes: inspectionModes,
      timeline: updatedTimeline,
      lasModelName,
      modelPoints,
      geometry: initialGeometry
    });
  };

  const handleZipDownload = async () => { if (sessionFindings.length === 0) return; setIsExportingZip(true); try { const allFindings: Finding[] = sessionFindings.flatMap(analysis => (analysis.findings || []).map(f => ({ ...f, frameImage: analysis.frameImage }))); await exportFindingsToZip(allFindings, "Simulation_Audit_Evidence"); } catch (e) { alert("Failed to generate ZIP."); } finally { setIsExportingZip(false); } };

  const handleContinue = () => {
    const finalVideo = segments.length > 0 ? segments[0].video : videoFile;
    let posterFrame = finalTimeline.length > 0 ? finalTimeline[0].frameImage : "";
    if (!posterFrame && videoRef.current && canvasRef.current) posterFrame = captureFrameForAnalysis(videoRef.current, canvasRef.current) || "";

    const verifiedTimestamps = new Set<number>();
    sessionFindings.forEach((sf, idx) => {
      if (verifiedFindingsIdx.has(idx)) verifiedTimestamps.add(sf.timestamp);
    });
    let verifiedTimeline = finalTimeline.filter(t => verifiedTimestamps.has(t.timestamp));
    if (verifiedTimeline.length === 0) verifiedTimeline = finalTimeline;

    onComplete(videoUrl || "", verifiedTimeline, finalVideo?.name || "Multi-Flight Mission", elapsedClock, 30, [rangeStart, rangeEnd], { posterFrame, file: finalVideo, trajectoryPoints: combinedTrajectory, lasModelName, modelPoints });
  };

  return (
    <div className="flex flex-col h-full bg-slate-300">
      <div className="p-4 border-b border-slate-500 flex justify-between items-center bg-slate-400 shadow-sm">
        <div className="flex flex-col text-left">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-none ${isProcessing ? 'bg-red-500 animate-pulse' : 'bg-[#0000a9] shadow-[0_0_10px_rgba(0,0,169,0.3)]'}`}></div>
            <h3 className="font-black text-slate-900 uppercase tracking-widest text-[10px] italic">MISSION CONTROL <span className="text-[#0000a9] not-italic mx-1">|</span> TELEMETRY AUDIT (INSPECTOR M)</h3>
          </div>
        </div>
        <button onClick={handleAbort} className="text-slate-700 hover:text-[#0000a9] transition uppercase text-[10px] font-black tracking-widest bg-slate-300 px-6 py-2 border border-slate-400 shadow-sm"> {complianceAccepted ? "Close Mission" : "Abort Mission"} </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col p-6 bg-slate-300 relative">
        {((videoUrl !== null || isSimulation) && complianceAccepted) ? (
          <>
            <div className="flex-1 min-h-0 flex w-full gap-6">
              <video ref={videoRef} src={videoUrl || undefined} onLoadedMetadata={handleMetadataLoaded} onSeeked={() => drawPreviewFrame()} preload="auto" className="hidden" muted playsInline />
              <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-slate-400 border border-slate-500 shadow-xl">
                <canvas ref={previewCanvasRef} className="max-h-full max-w-full rounded-none" />
                {isProcessing && (
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between pointer-events-none">
                    <div className="bg-slate-900/95 backdrop-blur-md border border-[#0000a9]/40 p-5 shadow-2xl text-left pointer-events-auto w-72 rounded-none">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">Audit Progress</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-none bg-slate-400 animate-pulse" />
                          <span className="text-[8px] font-mono text-slate-300 uppercase">Neural Active</span>
                        </div>
                      </div>
                      <span className="text-3xl font-black italic text-white block leading-none mb-1">{Math.floor(progress)}%</span>
                      <div className="w-full h-1.5 bg-slate-700 overflow-hidden mt-2 mb-3">
                        <div className="h-full bg-[#0000a9] transition-all duration-300 relative overflow-hidden" style={{ width: `${progress}%` }}>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-slate-700 pt-3">
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Elapsed</div>
                          <div className="text-[11px] font-black text-slate-200 font-mono">{Math.floor(elapsedClock / 60).toString().padStart(2, '0')}:{Math.floor(elapsedClock % 60).toString().padStart(2, '0')}</div>
                        </div>
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Time Left</div>
                          <div className="text-[11px] font-black text-slate-300 font-mono">
                            {progress > 2
                              ? (() => { const left = (elapsedClock / progress) * (100 - progress); return `${Math.floor(left / 60).toString().padStart(2, '0')}:${Math.floor(left % 60).toString().padStart(2, '0')}`; })()
                              : '--:--'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Anomalies</div>
                          <div className="text-[11px] font-black text-white font-mono">{sessionFindings.length}</div>
                        </div>
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Overlap</div>
                          <div className="text-[11px] font-black text-white font-mono">{forwardOverlap}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 relative bg-slate-400 border border-slate-500 overflow-hidden shadow-xl flex flex-col">
                <div className="p-4 border-b border-slate-500 bg-slate-500/20 flex justify-between items-center">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic leading-none">
                    {(isUAV && combinedTrajectory && combinedTrajectory.length > 0) || (modelPoints && modelPoints.length > 0) ? "Spatiotemporal Mission Map" : "Audit Evidence Ledger"}
                  </h4>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-[#0000a9] uppercase font-black leading-none">
                      {(isUAV && combinedTrajectory && combinedTrajectory.length > 0) || (modelPoints && modelPoints.length > 0) ? "Mission Sync Active" : `${sessionFindings.length} Anomalies Logged`}
                    </span>
                  </div>
                </div>
                <div className="flex-1 relative bg-slate-300 overflow-hidden">
                  {(isUAV && combinedTrajectory && combinedTrajectory.length > 0) || (modelPoints && modelPoints.length > 0) ? (
                    <TrajectoryMapM
                      points={combinedTrajectory}
                      minimal={false}
                      currentTime={currentGlobalMapTime}
                      baseTimestamp={0}
                      range={activeGlobalRange}
                      targetOverlap={forwardOverlap}
                      forceShowCaptureLocations={true}
                      modelPoints={modelPoints}
                      onClose={() => { }}
                      mapMode="PLANNING"
                      overrideGeometry={initialGeometry}
                      showCapOnly={showCapOnly}
                      initialCloudOffset={initialCloudOffset}
                    />
                  ) : (
                    <div className="absolute inset-0 overflow-y-auto p-4 custom-scrollbar bg-slate-300">
                      {sessionFindings.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center opacity-20 text-center p-8">
                          <svg className="w-12 h-12 mb-4 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          <span className="text-[10px] uppercase font-black tracking-widest text-slate-700 italic">Scanning mission footage...</span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 pb-8">
                          {[...sessionFindings].reverse().map((analysis, revIdx) => {
                            const fIdx = sessionFindings.length - 1 - revIdx;
                            const finding = analysis.findings?.[0];
                            const isVerified = verifiedFindingsIdx.has(fIdx);
                            const [ymin, xmin, ymax, xmax] = finding?.box || [0, 0, 1000, 1000];
                            const zoomScale = Math.min(6, 1000 / Math.max(1, xmax - xmin, ymax - ymin));
                            const color = severityColors[finding?.severity || Severity.NONE];

                            return (
                              <div key={fIdx} className={`bg-slate-300 border overflow-hidden group transition-all animate-slideUp flex flex-col relative ${isVerified ? 'border-[#0000a9] shadow-lg' : 'border-slate-500 shadow-sm hover:border-[#0000a9]/50'}`}>
                                {isVerified && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0000a9]"></div>}
                                <div className="flex h-32">
                                  <div className="w-1/3 bg-slate-900 relative overflow-hidden flex items-center justify-center border-r border-slate-500 rounded-none">
                                    {analysis.frameImage ? (
                                      <>
                                        <img src={analysis.frameImage} className="absolute max-w-none origin-top-left" style={{ width: `${zoomScale * 100}%`, height: 'auto', left: `${50 - ((xmin + xmax) / 2000) * zoomScale * 100}%`, top: `${50 - ((ymin + ymax) / 2000) * zoomScale * 100}%` }} alt="Defect" />
                                        {/* Overlay box: positioned at bbox center in zoomed-image space, sized proportional to bbox */}
                                        <div className="absolute pointer-events-none" style={{
                                          left: `${50 - ((xmax - xmin) / 2000) * zoomScale * 100}%`,
                                          top: `${50 - ((ymax - ymin) / 2000) * zoomScale * 100}%`,
                                          width: `${((xmax - xmin) / 1000) * zoomScale * 100}%`,
                                          height: `${((ymax - ymin) / 1000) * zoomScale * 100}%`,
                                        }}>
                                          <div className="absolute inset-0 border-2 border-red-500/70 shadow-[0_0_20px_rgba(239,68,68,0.4)] rounded-none" />
                                          <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-red-500 -translate-x-px -translate-y-px" />
                                          <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-red-500 translate-x-px -translate-y-px" />
                                          <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-red-500 -translate-x-px translate-y-px" />
                                          <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-red-500 translate-x-px translate-y-px" />
                                        </div>
                                      </>
                                    ) : (
                                      <div className="text-slate-700 text-[8px] font-black uppercase">No Data</div>
                                    )}
                                    <div className="absolute top-2 left-2 px-2 py-1 bg-slate-300/90 backdrop-blur text-[8px] font-black font-mono text-slate-900 border border-slate-400">T+{formatTime(analysis.timestamp || 0)}s</div>
                                  </div>
                                  <div className="flex-1 p-3 flex flex-col justify-between overflow-hidden">
                                    <div className="overflow-hidden">
                                      <div className="flex justify-between items-start mb-1 overflow-hidden">
                                        <h5 className="text-[10px] font-black text-slate-900 uppercase italic truncate pr-2 leading-none">{finding?.type || 'Structural Anomaly'}</h5>
                                        <div className="w-2 h-2 rounded-none shrink-0 shadow-[0_0_8px_currentColor]" style={{ backgroundColor: color, color }}></div>
                                      </div>
                                      <p className="text-[9px] text-slate-800 font-bold uppercase truncate leading-none mt-1">{analysis.description}</p>
                                    </div>
                                    <div className="flex gap-2 mt-auto pt-2 border-t border-slate-500">
                                      <button onClick={() => verifyFinding(fIdx)} className={`flex-1 py-1.5 rounded-none text-[8px] font-black uppercase tracking-widest transition-all ${isVerified ? 'bg-[#0000a9] text-white shadow-md' : 'bg-slate-300 text-slate-800 border border-slate-400 hover:border-[#0000a9]'}`}>
                                        {isVerified ? 'VERIFIED' : 'SAVE'}
                                      </button>
                                      <button onClick={() => removeFinding(fIdx)} className="px-2 py-1.5 bg-slate-300 text-slate-800 hover:text-red-500 border border-slate-400 rounded-none transition-all shadow-sm">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isProcessing ? (
              <div className="mt-4 px-4 shrink-0">
                <button
                  onClick={stopNeuralEngine}
                  disabled={isAborting}
                  className={`w-full py-5 rounded-none font-black uppercase text-[14px] tracking-[0.4em] shadow-2xl active:scale-[0.99] transition-all border-b-4 ${isAborting ? 'bg-red-900 text-white/50 cursor-not-allowed border-red-950' : 'bg-red-600 hover:bg-red-500 text-white animate-pulse border-red-700'}`}
                >
                  {isAborting ? 'HALTING NEURAL ENGINE...' : 'STOP NEURAL ENGINE'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4 mt-4 shrink-0 px-4">
                <button
                  onClick={handleContinue}
                  disabled={sessionFindings.length === 0}
                  className={`w-full py-5 rounded-none font-black uppercase text-[12px] tracking-[0.2em] shadow-sm transition-all border-b-4 ${sessionFindings.length === 0 ? 'bg-slate-300 text-slate-500 border-slate-400 cursor-not-allowed' : 'bg-[#0000a9]/10 hover:bg-[#0000a9] text-[#0000a9] hover:text-white border-[#0000a9] hover:shadow-xl'}`}
                >
                  VIEW AUDIT DETAILS
                </button>
                <button
                  onClick={() => runDeepAnalysis(true)}
                  className="w-full py-5 rounded-none font-black uppercase text-[12px] tracking-[0.2em] shadow-sm hover:shadow-xl transition-all border-b-4 bg-[#0000a9] hover:bg-[#0000c0] text-white border-blue-900"
                >
                  ADD INSPECTION AUDIT
                </button>
                <button
                  onClick={() => runDeepAnalysis(false)}
                  disabled={sessionFindings.length === 0}
                  className={`w-full py-5 rounded-none font-black uppercase text-[12px] tracking-[0.2em] shadow-sm transition-all border-b-4 ${sessionFindings.length === 0 ? 'bg-slate-300 text-slate-500 border-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-500 text-white border-red-700 hover:shadow-xl'}`}
                >
                  RE-AUDIT <span className="opacity-70 lowercase font-normal ml-1 tracking-normal">(clear old)</span>
                </button>
              </div>
            )}

            <div className="h-16 mt-4 bg-slate-400 shadow-xl rounded-none border border-slate-500 flex items-center px-6 gap-6 relative z-10 shrink-0">
              <div className="flex items-center gap-2"><button onClick={() => togglePlayback()} disabled={isProcessing} className={`w-10 h-10 rounded-none flex items-center justify-center transition-all shadow-md ${isPlaying ? 'bg-[#0000a9] text-white' : 'bg-slate-300 text-slate-900 border border-slate-400 hover:bg-[#0000a9] hover:text-white'}`} > {isPlaying ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg> : <svg className="w-5 h-5 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>} </button></div>
              <div className="flex items-center gap-4 bg-white px-4 py-1.5 rounded-none border border-slate-400 shadow-inner"><span className="text-[9px] font-black text-slate-700 uppercase">Sim Speed</span><input type="range" min="0.1" max="10.0" step="0.1" value={simulationSpeed} onChange={(e) => setSimulationSpeed(parseFloat(e.target.value))} className="w-24 h-1.5 bg-white rounded-none appearance-none accent-[#0000a9]" /></div>
              <div className="flex flex-col ml-auto text-right"><span className="text-[10px] text-slate-700 font-bold uppercase">Time Cursor</span><span className="text-sm font-mono font-black text-[#0000a9]">T+{formatTime(currentGlobalMapTime)}s</span></div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-12 bg-slate-300">
            <div className="w-full max-w-7xl aspect-video flex flex-col items-center justify-center gap-12 bg-slate-400 border border-slate-500 shadow-[0_40px_100px_rgba(0,0,169,0.1)] relative rounded-none">
              <h3 className="text-3xl font-black uppercase tracking-tighter text-[#0000a9] italic">Mission Initiation <span className="text-slate-700 font-light not-italic">| M Registry</span></h3>
              <div className="w-full max-w-6xl z-10 px-12 grid grid-cols-3 gap-12 text-left">
                <div className="flex flex-col gap-6 bg-slate-300 p-8 border border-slate-400 shadow-sm">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-2 leading-none">Flight Segments</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <button onClick={() => stageVideoRef.current?.click()} className={`group relative h-28 flex items-center gap-6 p-6 text-left transition-all border shadow-sm ${pendingVideo ? 'bg-slate-400 border-[#0000a9] shadow-[#0000a9]/10' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]'}`} >
                      <div className={`w-16 h-16 shrink-0 flex items-center justify-center transition-all ${pendingVideo ? 'bg-[#0000a9] text-white shadow-lg' : 'bg-slate-300 text-slate-800'}`}>
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" /></svg>
                      </div>
                      <div className="z-10 overflow-hidden">
                        <h3 className="font-black text-xs uppercase text-slate-900 tracking-wide mb-1">Source Video</h3>
                        <p className="text-[10px] text-slate-800 font-mono truncate uppercase font-bold">{pendingVideo ? pendingVideo.name : 'MP4/MOV TECHNICAL SOURCE'}</p>
                      </div>
                      <input type="file" ref={stageVideoRef} className="hidden" accept="video/*" onChange={handleStageVideo} />
                    </button>
                    {isUAV && (<button onClick={() => stageCsvRef.current?.click()} className={`group relative h-28 flex items-center gap-6 p-6 text-left transition-all border shadow-sm ${pendingTrajectory ? 'bg-slate-400 border-[#0000a9] shadow-[#0000a9]/10' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]'}`} >
                      <div className={`w-16 h-16 shrink-0 flex items-center justify-center transition-all ${pendingTrajectory ? 'bg-[#0000a9] text-white shadow-lg' : 'bg-slate-300 text-slate-800'}`}>
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 7m0 10V7m0 0L9 4" /></svg>
                      </div>
                      <div className="z-10 overflow-hidden">
                        <h3 className="font-black text-xs uppercase text-slate-900 tracking-wide mb-1">Telemetry</h3>
                        <p className="text-[10px] text-slate-800 font-mono uppercase font-bold">{pendingTrajectory ? 'CSV DATA VALIDATED' : 'UAV FLIGHT LOG CSV'}</p>
                      </div>
                      <input type="file" ref={stageCsvRef} className="hidden" accept=".csv" onChange={handleStageCsv} />
                    </button>)}
                  </div>
                  <button onClick={addFlightSegment} disabled={!pendingVideo || (isUAV && !pendingTrajectory)} className={`w-full py-5 mt-2 font-black uppercase text-[11px] tracking-widest transition-all flex items-center justify-center gap-3 shadow-lg ${pendingVideo && (pendingTrajectory || !isUAV) ? 'bg-[#0000a9] text-white hover:bg-[#0000c0]' : 'bg-slate-400 text-slate-800 opacity-80'}`} >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg> ENCODE SEGMENT
                  </button>
                </div>
                <div className="flex flex-col gap-6 bg-slate-300 p-8 border border-slate-400 shadow-sm overflow-hidden">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-2 leading-none">Flight Manifest</h4>
                  <div className="flex-1 overflow-y-auto max-h-[300px] custom-scrollbar space-y-4 pr-3">
                    {segments.length === 0 ? <div className="h-full min-h-[160px] flex items-center justify-center text-[11px] text-slate-800 font-black uppercase border border-dashed border-slate-500 bg-slate-400 shadow-inner">MANIFEST_NULL</div> : segments.map((s, i) => <div key={s.id} className="p-4 border bg-slate-400 border-slate-500 shadow-sm flex justify-between items-center"><div className="text-[10px] font-black uppercase italic text-slate-700">SEG_{i + 1} | {s.video.name}</div><button onClick={() => removeSegment(s.id)} className="p-2 text-slate-800 hover:text-red-500 transition-colors bg-slate-300 border border-slate-400"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg></button></div>)}
                  </div>
                </div>
                <div className="flex flex-col gap-6 bg-slate-300 p-8 border border-slate-400 shadow-sm">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-2 leading-none">Global Asset Registry</h4>
                  <div className="flex-1">
                    <button onClick={() => lasInputRef.current?.click()} className={`group relative w-full h-full flex flex-col justify-center items-center gap-8 p-10 text-center transition-all border shadow-sm ${lasModelName ? 'bg-slate-400 border-[#0000a9] shadow-[#0000a9]/5' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]'}`} >
                      <div className={`w-20 h-20 flex items-center justify-center transition-all ${lasModelName ? 'bg-[#0000a9] text-white shadow-lg' : 'bg-slate-300 text-slate-800'}`}>
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 00-2-2v-6a2 2 0 01-2-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </div>
                      <div className="z-10 overflow-hidden text-center">
                        <h3 className="font-black text-xs uppercase text-slate-900 tracking-wide mb-1">{lasModelName ? 'Source Linked' : 'IMPORT POINT CLOUD'}</h3>
                        <p className="text-[10px] text-slate-800 font-mono truncate px-4 font-bold uppercase">{lasModelName || '.LAS / .LAZ / .XYZ / .TXT'}</p>
                      </div>
                      <input type="file" ref={lasInputRef} className="hidden" accept=".las,.laz,.xyz,.txt,.csv" onChange={handleLasUpload} />
                    </button>
                  </div>
                </div>
              </div>
              <button disabled={!canProceed} onClick={() => setComplianceAccepted(true)} className={`w-full max-w-7xl py-10 font-black uppercase text-[15px] tracking-[0.6em] transition-all italic border-b-4 ${canProceed ? 'bg-[#0000a9] text-white shadow-[0_30px_60px_rgba(0,0,169,0.2)] border-blue-900 hover:translate-y-[-2px]' : 'bg-slate-300 text-slate-800 border-slate-400 cursor-not-allowed'}`} > EXECUTE SIMULATION AUDIT </button>
            </div>
          </div>
        )}
      </div>

      {((videoUrl !== null || isSimulation) && complianceAccepted) && (
        <div className="p-8 bg-slate-400 border-t border-slate-500 space-y-10 overflow-y-auto max-h-[35vh] custom-scrollbar text-left">
          <div className="transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="space-y-6">
                <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic leading-none">Global Mission Protocol</h4>
                <div className="grid grid-cols-2 gap-4 w-full">
                  {PROTOCOL_OPTIONS.map(protocol => {
                    const isActive = inspectionModes.includes(protocol.id);
                    return (
                      <button
                        key={protocol.id}
                        onClick={() => toggleMode(protocol.id)}
                        className={`relative group p-5 border text-left transition-all overflow-hidden rounded-none shadow-sm ${isActive ? 'bg-[#0000a9] border-[#0000a9] shadow-[0_0_15px_rgba(0,0,169,0.3)]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/40 hover:bg-slate-400'}`}
                      >
                        <div className="flex flex-col h-full justify-between gap-4 relative z-10">
                          <div className="flex justify-between items-start">
                            <div className={`w-8 h-8 transition-colors duration-300 ${isActive ? 'text-[#0000a9]' : 'text-slate-300 group-hover:text-slate-800'}`}>
                              {protocol.icon}
                            </div>
                            {isActive && <div className="w-2 h-2 bg-[#0000a9] animate-pulse"></div>}
                          </div>
                          <div>
                            <h3 className={`font-black text-[10px] uppercase tracking-widest leading-none mb-1 ${isActive ? 'text-white' : 'text-slate-900 group-hover:text-[#0000a9]'}`}>
                              {protocol.label}
                            </h3>
                            <p className={`text-[9px] font-bold leading-tight uppercase tracking-tight ${isActive ? 'text-white/80' : 'text-slate-800'}`}>
                              {protocol.desc}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="pt-2">
                  <div className="flex items-center justify-between p-6 bg-slate-300 border border-slate-400 shadow-sm rounded-none">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black text-[#0000a9] uppercase tracking-widest leading-none">Temporal Windowing Filter</span>
                      <span className="text-[9px] text-slate-800 font-bold uppercase tracking-tight">Show frustums only within range</span>
                    </div>
                    <button
                      onClick={() => setShowCapOnly(!showCapOnly)}
                      className={`w-14 h-7 rounded-none transition-all relative p-1 shadow-inner ${showCapOnly ? 'bg-[#0000a9]' : 'bg-slate-400 border border-slate-500'}`}
                    >
                      <div className={`w-5 h-5 transition-all shadow-md ${showCapOnly ? 'bg-slate-300 translate-x-7' : 'bg-slate-300 translate-x-0'}`}></div>
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic text-left leading-none">Mission Spatiotemporal Range</h4>
                <div className="space-y-8 pt-2">
                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Mission Inception</span> <span className="text-[#0000a9]">{formatTime(rangeStart)}s</span> </div>
                    <input type="range" min="0" max={isSimulation ? trajectoryDuration : (videoDuration || 1)} step="0.1" value={rangeStart} onChange={(e) => handleRangeChange(e, true)} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} />
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Mission Termination</span> <span className="text-red-500">{formatTime(rangeEnd)}s</span> </div>
                    <input type="range" min="0" max={isSimulation ? trajectoryDuration : (videoDuration || 1)} step="0.1" value={rangeEnd} onChange={(e) => handleRangeChange(e, false)} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-red-500 ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} />
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <div className="space-y-8">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic text-left leading-none">Technical Parameters</h4>
                  <div className="space-y-4 pt-1"> <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"><span className="text-slate-700">Audit Sensitivity</span><span className="text-[#0000a9]">LVL {sensitivity}</span></div> <input type="range" min="1" max="10" step="1" value={sensitivity} onChange={(e) => setSensitivity(parseInt(e.target.value))} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} /> </div>
                  <div className="space-y-4 pt-1"> <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Reconstruction Overlap</span> <span className="text-[#0000a9]">{forwardOverlap}%</span> </div> <input type="range" min="0" max="50" step="1" value={forwardOverlap} onChange={(e) => setForwardOverlap(parseInt(e.target.value))} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} /> </div>
                  <div className="space-y-3 pt-1">
                    <div className="text-[11px] font-black uppercase tracking-widest text-slate-700">Neural Resolution</div>
                    <div className="flex flex-col gap-2">
                      {IMAGE_RESOLUTION_OPTIONS.map(opt => (
                        <button
                          key={opt.mode}
                          type="button"
                          onClick={() => !isProcessing && setResolutionMode(opt.mode)}
                          disabled={isProcessing}
                          className={`w-full text-left px-4 py-2.5 border transition-all flex justify-between items-center rounded-none shadow-sm ${resolutionMode === opt.mode
                            ? 'bg-[#0000a9] border-[#0000a9] text-white'
                            : 'bg-slate-300 border-slate-400 text-slate-800 hover:border-[#0000a9]/60'
                            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                        >
                          <span className="text-[10px] font-black uppercase tracking-widest">{opt.label}</span>
                          <span className={`text-[9px] font-bold uppercase tracking-tight ${resolutionMode === opt.mode ? 'text-white/70' : 'text-slate-600'}`}>{opt.desc.split('·').pop()?.trim()}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Neural Engine controls moved higher */}
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
      {isArchiving && <LoadingOverlay message="ARCHIVING MISSION LOGS" subMessage="COMMITTING MULTI-MISSION ASSETS TO LOCAL STORAGE" />}
    </div>
  );
};